import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleCheck, CircleX, Clock, CreditCard, XCircle } from 'lucide-react';
import { PaymentProviderId } from '../types';
import { mockGateway } from '../providers/mock.provider';

/**
 * ─── Fausse page de paiement hébergée (MockProvider uniquement) ─────────────
 *
 * Simule la page de paiement FlexPay : l'utilisateur choisit l'issue de la
 * transaction, puis est redirigé vers /payment/return exactement comme le
 * ferait le vrai provider (approve_url / cancel_url / decline_url).
 * Cette page n'est JAMAIS atteignable hors mode mock.
 */
export function MockGatewayPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderNumber = params.get('orderNumber') ?? '';
  const reference = params.get('reference') ?? '';
  const tx = mockGateway.getTransaction(orderNumber);

  const goBack = (outcome: 'approved' | 'cancelled' | 'declined') => {
    navigate(
      `/payment/return?provider=${PaymentProviderId.MOCK}&outcome=${outcome}` +
        `&reference=${encodeURIComponent(reference)}&orderNumber=${encodeURIComponent(orderNumber)}`,
      { replace: true },
    );
  };

  const scenarios = [
    {
      label: 'Payer — transaction acceptée',
      icon: <CircleCheck className="w-4 h-4" />,
      style: 'bg-gradient-to-r from-green-500 to-emerald-600 text-white',
      run: () => {
        mockGateway.chooseScenario(orderNumber, 'success');
        goBack('approved');
      },
    },
    {
      label: 'Payer — refusée par la banque',
      icon: <CircleX className="w-4 h-4" />,
      style: 'bg-red-500/15 border border-red-500/40 text-red-400',
      run: () => {
        mockGateway.chooseScenario(orderNumber, 'declined');
        goBack('approved'); // approuvé sur la page, mais la vérification échouera
      },
    },
    {
      label: 'Payer — rester en attente (timeout)',
      icon: <Clock className="w-4 h-4" />,
      style: 'bg-amber-500/15 border border-amber-500/40 text-amber-400',
      run: () => {
        mockGateway.chooseScenario(orderNumber, 'stay_pending');
        goBack('approved');
      },
    },
    {
      label: 'Refus immédiat (decline_url)',
      icon: <CircleX className="w-4 h-4" />,
      style: 'bg-overlay-2 border border-line text-fg-muted',
      run: () => goBack('declined'),
    },
    {
      label: 'Annuler le paiement (cancel_url)',
      icon: <XCircle className="w-4 h-4" />,
      style: 'bg-overlay-2 border border-line text-fg-muted',
      run: () => goBack('cancelled'),
    },
  ];

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-overlay-1 border border-line rounded-2xl p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CreditCard className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-lg font-bold text-fg">Page de paiement simulée</h1>
          <p className="text-xs text-fg-subtle mt-2 leading-relaxed">
            Mode test — aucun paiement réel. Choisissez le scénario à simuler
            pour la transaction ci-dessous.
          </p>
        </div>

        <div className="bg-overlay-2 border border-line rounded-xl px-4 py-3 mb-6 text-xs text-fg-muted space-y-1">
          <p>Référence : <span className="font-mono text-fg">{reference || '—'}</span></p>
          <p>Order : <span className="font-mono text-fg">{orderNumber || '—'}</span></p>
          {tx && (
            <p>Montant : <span className="font-semibold text-fg">{tx.amount} {tx.currency}</span></p>
          )}
        </div>

        <div className="space-y-2.5">
          {scenarios.map((s) => (
            <button
              key={s.label}
              onClick={s.run}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold active:scale-[0.98] transition-all ${s.style}`}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
